import { RenderType, TFormFieldsAndRules_New, TFromFieldInfo_New } from '@verifime/components';

import { api } from '@verifime/api-definition';
import {
  Australia,
  getStateItemsForCountry,
  OtherIdentityDocumentOptions,
  SupportedCountryCode,
  TIdentityDocumentType,
  TOption,
  validationRules,
} from '@verifime/utils';
import * as yup from 'yup';

const mapCountryOptions = (data: { alpha3?: string | null; name?: string | null }[]) => {
  return data
    .filter((item) => Boolean(item.name))
    .map((item) => ({ code: item.alpha3 ?? '', label: item.name ?? '' }));
};

const searchCountryOptions = (value: string) =>
  api.getV1referenceauthorizedIssuerssearch({ queries: { term: value } }).then(mapCountryOptions);

// Driver License
export type TDriverLicenceFields =
  | 'licenceFirstName'
  | 'licenceMiddleName'
  | 'licenceFamilyName'
  | 'licenceCountryOfIssue'
  | 'licenceState'
  | 'licenceNumber'
  | 'licenceCardNumber'
  | 'licenceVersionNumber'
  | 'licenceExpiry';

const createDriverLicenceFormFields = (): {
  [field in TDriverLicenceFields]: TFromFieldInfo_New;
} => {
  return {
    licenceFirstName: {
      label: 'First Name on Your Licence',
      fieldName: 'licenceFirstName',
      renderType: RenderType.Text,
      rules: validationRules.OPTIONAL_STRING_RULES,
    },
    licenceMiddleName: {
      label: 'Middle Name on Your Licence',
      fieldName: 'licenceMiddleName',
      renderType: RenderType.Text,
      rules: validationRules.OPTIONAL_STRING_RULES,
    },
    licenceFamilyName: {
      label: 'Family Name on Your Licence',
      fieldName: 'licenceFamilyName',
      renderType: RenderType.Text,
      rules: validationRules.OPTIONAL_STRING_RULES,
    },
    licenceCountryOfIssue: {
      label: 'Country of issue',
      fieldName: 'licenceCountryOfIssue',
      rules: validationRules.REQUIRED_STRING_RULES,
      renderType: RenderType.ComboBox,
      optionProvider: searchCountryOptions,
    },
    licenceState: {
      label: 'State',
      fieldName: 'licenceState',
      renderType: RenderType.Select,
      rules: yup.mixed().when(['licenceCountryOfIssue'], ([countryOfIssueCode]) => {
        return validationRules.countryStateSelectInputRule(countryOfIssueCode);
      }),
      items: getStateItemsForCountry(''),
    },
    licenceNumber: {
      label: 'Licence number',
      fieldName: 'licenceNumber',
      renderType: RenderType.Text,
      rules: validationRules.REQUIRED_DRIVER_LICENCE_NUMBER_VALIDATION_RULES,
    },
    licenceCardNumber: {
      label: 'Licence Card Number (AU)',
      fieldName: 'licenceCardNumber',
      renderType: RenderType.Text,
      // No default rule here: generateFormFieldsAndRulesWithLogic in OcrEditor.tsx always
      // assigns this field's rules (required for an Australian licence, dropped otherwise)
      // before this config reaches any consumer.
    },
    licenceVersionNumber: {
      label: 'Licence Version Number (NZ)',
      fieldName: 'licenceVersionNumber',
      renderType: RenderType.Text,
      // No default rule here: generateFormFieldsAndRulesWithLogic in OcrEditor.tsx always
      // assigns this field's rules (required for a New Zealand licence, dropped otherwise)
      // before this config reaches any consumer.
    },
    licenceExpiry: {
      label: 'Licence expiry',
      fieldName: 'licenceExpiry',
      renderType: RenderType.Date,
      rules: validationRules.REQUIRED_EXPIRY_DATE_VALIDATION_RULES,
    },
  };
};

// Photo Card: an Australian state or territory photo card or proof of age card. It carries the
// driver licence field set without the card number (no DVS check) and without the New Zealand
// version number. The country of issue is always Australia, so it is shown and not choosable.
// The expiry is optional because two templates print none.
//
// Victoria prints a full name only, so the scan can leave the first and family name empty. The
// customer fills them in, and the first and family name are required to submit (the middle name
// is not).
//
// Every label below is an UNAPPROVED DRAFT (verifime-adr#816): to be approved with the
// stakeholder in the running build.
export type TPhotoCardFields =
  | 'photoCardFirstName'
  | 'photoCardMiddleName'
  | 'photoCardFamilyName'
  | 'photoCardCountryOfIssue'
  | 'photoCardState'
  | 'photoCardNumber'
  | 'photoCardExpiry';

const createPhotoCardFormFields = (): {
  [field in TPhotoCardFields]: TFromFieldInfo_New;
} => {
  return {
    photoCardFirstName: {
      // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
      label: 'First Name on Your Card',
      fieldName: 'photoCardFirstName',
      renderType: RenderType.Text,
      rules: validationRules.REQUIRED_STRING_RULES,
    },
    photoCardMiddleName: {
      // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
      label: 'Middle Name on Your Card',
      fieldName: 'photoCardMiddleName',
      renderType: RenderType.Text,
      rules: validationRules.OPTIONAL_STRING_RULES,
    },
    photoCardFamilyName: {
      // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
      label: 'Family Name on Your Card',
      fieldName: 'photoCardFamilyName',
      renderType: RenderType.Text,
      rules: validationRules.REQUIRED_STRING_RULES,
    },
    photoCardCountryOfIssue: {
      // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
      label: 'Country of issue',
      fieldName: 'photoCardCountryOfIssue',
      renderType: RenderType.Select,
      rules: validationRules.REQUIRED_STRING_RULES,
      items: [{ code: Australia.code, label: Australia.label }],
      disabled: true,
    },
    photoCardState: {
      // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
      label: 'State',
      fieldName: 'photoCardState',
      renderType: RenderType.Select,
      rules: validationRules.countryStateSelectInputRule(SupportedCountryCode.AUS),
      items: getStateItemsForCountry(SupportedCountryCode.AUS),
    },
    photoCardNumber: {
      // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
      label: 'Photo Card number',
      fieldName: 'photoCardNumber',
      renderType: RenderType.Text,
      rules: validationRules.REQUIRED_PHOTO_CARD_NUMBER_VALIDATION_RULES,
    },
    photoCardExpiry: {
      // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
      label: 'Card expiry',
      fieldName: 'photoCardExpiry',
      renderType: RenderType.Date,
      rules: validationRules.OPTIONAL_EXPIRY_DATE_VALIDATION_RULES,
    },
  };
};

// Medicare Card
export const MEDICARE_CARD_COLOURS = ['Green', 'Blue', 'Yellow'];
export type TMedicareCardFields =
  | 'medicareCardNumber'
  | 'medicareCardColour'
  | 'medicareCardExpiry'
  | 'medicareCardIndividualRefNumber'
  | 'medicareCardFullName';

export const medicareCardFormFields: { [field in TMedicareCardFields]: TFromFieldInfo_New } = {
  medicareCardNumber: {
    label: 'Medicare Card Number',
    fieldName: 'medicareCardNumber',
    renderType: RenderType.Text,
    rules: validationRules.requiredStringRule({ min: 10, max: 11 }),
  },
  medicareCardColour: {
    label: 'Medicare Card Colour',
    fieldName: 'medicareCardColour',
    rules: validationRules.REQUIRED_STRING_RULES,
    renderType: RenderType.Select,
    items: MEDICARE_CARD_COLOURS.map((colour) => ({ code: colour, label: colour }) as TOption),
  },
  medicareCardExpiry: {
    label: 'Medicare Card Expiry',
    fieldName: 'medicareCardExpiry',
    renderType: RenderType.Date,
    rules: validationRules.requiredDateRule(),
  },
  medicareCardIndividualRefNumber: {
    label: 'Individual Reference Number',
    fieldName: 'medicareCardIndividualRefNumber',
    rules: validationRules.REQUIRED_SINGLE_DIGIT_1_TO_9_VALIDATION_RULES,
    renderType: RenderType.Number,
  },
  medicareCardFullName: {
    label: 'Full Name (as it appears on card)',
    fieldName: 'medicareCardFullName',
    rules: validationRules.REQUIRED_FULL_NAME_VALIDATION_RULES,
    renderType: RenderType.Text,
  },
};

export const medicareCardFieldsAndRules: TFormFieldsAndRules_New = {
  row1: [medicareCardFormFields.medicareCardNumber],
  row2: [medicareCardFormFields.medicareCardColour],
  row3: [medicareCardFormFields.medicareCardExpiry],
  row4: [medicareCardFormFields.medicareCardIndividualRefNumber],
  row5: [medicareCardFormFields.medicareCardFullName],
};

// Passport
export type TPassportFields =
  | 'passportNumber'
  | 'passportGivenNames'
  | 'passportFamilyName'
  | 'passportCountryOfIssue'
  | 'passportExpiry';

const createPassportFormFields = (): { [field in TPassportFields]: TFromFieldInfo_New } => {
  return {
    passportNumber: {
      label: 'Passport Number',
      fieldName: 'passportNumber',
      renderType: RenderType.Text,
      rules: validationRules.REQUIRED_STRING_RULES,
    },
    passportGivenNames: {
      label: 'Given Names on Your Passport',
      fieldName: 'passportGivenNames',
      renderType: RenderType.Text,
      rules: validationRules.OPTIONAL_STRING_RULES,
    },
    passportFamilyName: {
      label: 'Family Name on Your Passport',
      fieldName: 'passportFamilyName',
      renderType: RenderType.Text,
      rules: validationRules.OPTIONAL_STRING_RULES,
    },
    passportCountryOfIssue: {
      label: 'Country of Issue',
      fieldName: 'passportCountryOfIssue',
      rules: validationRules.REQUIRED_STRING_RULES,
      renderType: RenderType.ComboBox,
      optionProvider: searchCountryOptions,
    },
    passportExpiry: {
      label: 'Expiry Date',
      fieldName: 'passportExpiry',
      renderType: RenderType.Date,
      rules: validationRules.REQUIRED_EXPIRY_DATE_VALIDATION_RULES,
    },
  };
};

// ImmiCard (Australian Department of Home Affairs): passport-shaped, issued by Australia, so no
// country, state or licence-style card number. The given names are optional because a card can
// print none. The number is checked after it is upper-cased and stripped of spaces, and the
// payload sends it in that form.
//
// Every label below is an UNAPPROVED DRAFT (verifime-adr#816): to be approved with the
// stakeholder in the running build.
export type TImmiCardFields =
  | 'immiCardNumber'
  | 'immiCardGivenNames'
  | 'immiCardFamilyName'
  | 'immiCardExpiry';

const createImmiCardFormFields = (): { [field in TImmiCardFields]: TFromFieldInfo_New } => {
  return {
    immiCardNumber: {
      // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
      label: 'ImmiCard Number',
      fieldName: 'immiCardNumber',
      renderType: RenderType.Text,
      rules: validationRules.REQUIRED_IMMI_CARD_NUMBER_VALIDATION_RULES,
    },
    immiCardGivenNames: {
      // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
      label: 'Given Names on Your ImmiCard',
      fieldName: 'immiCardGivenNames',
      renderType: RenderType.Text,
      rules: validationRules.OPTIONAL_STRING_RULES,
    },
    immiCardFamilyName: {
      // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
      label: 'Family Name on Your ImmiCard',
      fieldName: 'immiCardFamilyName',
      renderType: RenderType.Text,
      rules: validationRules.REQUIRED_STRING_RULES,
    },
    immiCardExpiry: {
      // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
      label: 'Expiry Date',
      fieldName: 'immiCardExpiry',
      renderType: RenderType.Date,
      rules: validationRules.REQUIRED_EXPIRY_DATE_VALIDATION_RULES,
    },
  };
};

// Other Identity Document
export type TOtherIdentityDocumentFields =
  | 'otherDocumentType'
  | 'documentIdentifier'
  | 'countryOfIssue'
  | 'dateOfIssue'
  | 'dateOfExpiry';

const getOtherIdentityDocumentOptionsFromEnv = (): TOption[] => {
  const givenTypes = process.env.NEXT_PUBLIC_OTHER_DOCUMENT_TYPES?.split(',');

  if (!givenTypes) {
    return [];
  }

  return givenTypes
    .reduce((tally, type) => [...tally, OtherIdentityDocumentOptions[type]], [])
    .filter(Boolean);
};

const createOtherIdentityDocumentFormFields = (): {
  [field in TOtherIdentityDocumentFields]: TFromFieldInfo_New;
} => {
  return {
    otherDocumentType: {
      label: 'Document Type',
      fieldName: 'otherDocumentType',
      renderType: RenderType.Select,
      rules: validationRules.REQUIRED_STRING_RULES,
      items: getOtherIdentityDocumentOptionsFromEnv(),
    },
    documentIdentifier: {
      label: 'Unique ID or number',
      fieldName: 'documentIdentifier',
      rules: validationRules.REQUIRED_STRING_RULES,
      renderType: RenderType.Text,
    },
    countryOfIssue: {
      label: 'Country of issue',
      fieldName: 'countryOfIssue',
      rules: validationRules.REQUIRED_STRING_RULES,
      renderType: RenderType.ComboBox,
      optionProvider: searchCountryOptions,
    },
    dateOfIssue: {
      label: 'Date of issue',
      fieldName: 'dateOfIssue',
      renderType: RenderType.Date,
      rules: validationRules.requiredDateRule(),
    },
    dateOfExpiry: {
      label: 'Date of expiry',
      fieldName: 'dateOfExpiry',
      renderType: RenderType.Date,
      rules: validationRules.REQUIRED_EXPIRY_DATE_VALIDATION_RULES,
    },
  };
};

export type TOcrFormFields =
  | TDriverLicenceFields
  | TPhotoCardFields
  | TMedicareCardFields
  | TPassportFields
  | TImmiCardFields
  | TOtherIdentityDocumentFields;

export const getFormFieldConfigMap = (): Record<
  TIdentityDocumentType,
  Record<string, TFromFieldInfo_New>
> => {
  return {
    DriverLicence: createDriverLicenceFormFields(),
    PhotoCard: createPhotoCardFormFields(),
    MedicareCard: medicareCardFormFields,
    Passport: createPassportFormFields(),
    ImmiCard: createImmiCardFormFields(),
    OtherIdentityDocument: createOtherIdentityDocumentFormFields(),
  };
};
